import React, { useState } from 'react';
import { Button } from '../../../shared/components/ui';
import { formatCurrency, formatNumber } from '../../../shared/utils/formatters';
import { SimilarityBreakdownModal } from './SimilarityBreakdownModal';

function SimilarityScoreBar({ score }) {
  const percentage = (score * 100).toFixed(0);
  const color = score > 0.8 ? 'bg-green-500' : score > 0.6 ? 'bg-yellow-500' : 'bg-gray-400';

  return (
    <div className="flex items-center gap-2">
      <div className="w-24 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
        <div className={`h-full ${color}`} style={{ width: `${percentage}%` }} />
      </div>
      <span className="text-xs font-medium">{percentage}%</span>
    </div>
  );
}

export function NearestNeighborsPanel({ item, neighbors }) {
  const [selectedNeighbor, setSelectedNeighbor] = useState(null);

  if (!neighbors || neighbors.length === 0) {
    return (
      <div className="text-center py-6 text-gray-500 dark:text-gray-400">
        <p>No similar line items found in historical estimates.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-semibold text-gray-900 dark:text-gray-100">
          {neighbors.length} Most Similar Line Items from Historical Estimates
        </h4>
        <div className="text-xs text-gray-500 dark:text-gray-400">
          Source: {item.description}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm border border-gray-200 dark:border-gray-800 rounded-lg">
          <thead className="bg-gray-100 dark:bg-gray-800">
            <tr>
              <th className="text-left py-2 px-3 font-medium text-gray-700 dark:text-gray-200">Project</th>
              <th className="text-left py-2 px-3 font-medium text-gray-700 dark:text-gray-200">Description</th>
              <th className="text-right py-2 px-3 font-medium text-gray-700 dark:text-gray-200">Quantity</th>
              <th className="text-right py-2 px-3 font-medium text-gray-700 dark:text-gray-200">Unit Cost</th>
              <th className="text-right py-2 px-3 font-medium text-gray-700 dark:text-gray-200">Total Cost</th>
              <th className="text-right py-2 px-3 font-medium text-gray-700 dark:text-gray-200">Variance</th>
              <th className="text-center py-2 px-3 font-medium text-gray-700 dark:text-gray-200">Match Score</th>
              <th className="text-center py-2 px-3 font-medium text-gray-700 dark:text-gray-200">Details</th>
            </tr>
          </thead>
          <tbody>
            {neighbors.map((neighbor, idx) => {
              const variance = item.unitCost && neighbor.unit_cost
                ? ((neighbor.unit_cost - item.unitCost) / item.unitCost) * 100
                : null;

              return (
                <tr key={idx} className="border-t border-gray-200 dark:border-gray-800 hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors">
                  <td className="py-2 px-3">
                    <div>
                      <div className="font-medium text-gray-900 dark:text-gray-100 truncate max-w-[200px]">
                        {neighbor.project_name}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {neighbor.project_date}
                      </div>
                    </div>
                  </td>
                  <td className="py-2 px-3">
                    <div className="max-w-[250px] truncate" title={neighbor.description}>
                      {neighbor.description}
                    </div>
                  </td>
                  <td className="py-2 px-3 text-right text-gray-600 dark:text-gray-300">
                    {formatNumber(neighbor.quantity)} {neighbor.unit}
                  </td>
                  <td className="py-2 px-3 text-right text-gray-900 dark:text-gray-100 font-medium">
                    {formatCurrency(neighbor.unit_cost)}
                  </td>
                  <td className="py-2 px-3 text-right text-gray-900 dark:text-gray-100">
                    {formatCurrency(neighbor.total_cost)}
                  </td>
                  <td className={`py-2 px-3 text-right font-medium ${
                    variance !== null
                      ? variance > 0
                        ? 'text-red-600 dark:text-red-400'
                        : 'text-green-600 dark:text-green-400'
                      : 'text-gray-400 dark:text-gray-500'
                  }`}>
                    {variance !== null ? (
                      `${variance > 0 ? '+' : ''}${variance.toFixed(1)}%`
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="py-2 px-3">
                    <div className="flex justify-center">
                      <SimilarityScoreBar score={neighbor.similarity_score} />
                    </div>
                  </td>
                  <td className="py-2 px-3 text-center">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setSelectedNeighbor(neighbor)}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Cost Analysis Summary */}
      {neighbors.length > 0 && item.unitCost && (
        <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
          <h5 className="font-semibold text-blue-900 dark:text-blue-200 mb-2">Cost Analysis</h5>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-blue-600 dark:text-blue-400">Your Unit Cost</p>
              <p className="font-medium text-blue-900 dark:text-blue-200">{formatCurrency(item.unitCost)}</p>
            </div>
            <div>
              <p className="text-blue-600 dark:text-blue-400">Avg Similar Cost</p>
              <p className="font-medium text-blue-900 dark:text-blue-200">
                {formatCurrency(
                  neighbors.reduce((sum, n) => sum + n.unit_cost, 0) / neighbors.length
                )}
              </p>
            </div>
            <div>
              <p className="text-blue-600 dark:text-blue-400">Min Cost</p>
              <p className="font-medium text-blue-900 dark:text-blue-200">
                {formatCurrency(Math.min(...neighbors.map(n => n.unit_cost)))}
              </p>
            </div>
            <div>
              <p className="text-blue-600 dark:text-blue-400">Max Cost</p>
              <p className="font-medium text-blue-900 dark:text-blue-200">
                {formatCurrency(Math.max(...neighbors.map(n => n.unit_cost)))}
              </p>
            </div>
          </div>
        </div>
      )}

      {selectedNeighbor && (
        <SimilarityBreakdownModal
          neighbor={selectedNeighbor}
          sourceItem={item}
          onClose={() => setSelectedNeighbor(null)}
        />
      )}
    </div>
  );
}

export default NearestNeighborsPanel;
